import { TICK_DURATION } from "@/config/constants";
import type { FrameName } from "@/game/entities/config";
import { ResourceType } from "@/game/entities/config/resources";
import type { ResourcesStoreState } from "@/store/useResourcesStore";
import type { EventMilestoneRewardConfig } from "@/types";

const ticksPerMinute = 60 / TICK_DURATION;

/** Utilities have no passive income — incomeMinutes is ignored for these. */
const UTILITIES_WITHOUT_INCOME = new Set<ResourceType>([
	ResourceType.GEMS,
	ResourceType.SUPER_CLICKER,
	ResourceType.AD_SKIPPER,
]);

export enum GrandPrizeRewardType {
	CURRENCY = "currency",
	ITEM = "item",
}

export type GrandPrizeInGameRewardCurrency = {
	type: GrandPrizeRewardType.CURRENCY;
	currency: ResourceType;
	amount: number;
};

export type GrandPrizeInGameRewardItem = {
	type: GrandPrizeRewardType.ITEM;
	frameName: FrameName;
	amount: number;
};

export type GrandPrizeInGameReward =
	| GrandPrizeInGameRewardCurrency
	| GrandPrizeInGameRewardItem;

function resolveGrandPrizeCurrencyType(
	reward: EventMilestoneRewardConfig,
): ResourceType | null {
	if (reward.type !== "currency") return null;

	const rawCurrency = reward.currency?.trim();
	if (!rawCurrency) return null;

	return Object.values(ResourceType).includes(rawCurrency as ResourceType)
		? (rawCurrency as ResourceType)
		: null;
}

function incomePerTickForCurrency(
	currency: ResourceType,
	resources: ResourcesStoreState,
): number {
	if (UTILITIES_WITHOUT_INCOME.has(currency)) return 0;

	const incomeKey = `${currency}UncappedIncome` as ResourceType;
	if (!Object.values(ResourceType).includes(incomeKey)) return 0;

	const income = resources[incomeKey];

	return Number.isFinite(income) ? income : 0;
}

/** Resolve chest currency amount — uses that resource's *Income, not coinsIncome. */
export function resolveGrandPrizeCurrencyAmount(
	reward: EventMilestoneRewardConfig,
	resources: ResourcesStoreState,
): number {
	if (reward.type !== "currency") return 0;

	const fixedAmount = reward.amount ?? 0;
	const incomeMinutes = (reward.incomeMinutes ?? 1) * (reward.amount ?? 1);
	if (typeof incomeMinutes !== "number" || incomeMinutes <= 0) {
		return Math.max(0, fixedAmount);
	}

	const currency = resolveGrandPrizeCurrencyType(reward);
	if (!currency) return Math.max(0, fixedAmount);

	const dynamicAmount =
		incomePerTickForCurrency(currency, resources) *
		ticksPerMinute *
		incomeMinutes;

	return Math.max(0, fixedAmount, dynamicAmount);
}

export function toGrandPrizeInGameReward(
	reward: EventMilestoneRewardConfig,
	resources: ResourcesStoreState,
): GrandPrizeInGameReward | null {
	if (reward.type === "currency") {
		const currency = resolveGrandPrizeCurrencyType(reward);
		if (!currency) return null;

		const amount = resolveGrandPrizeCurrencyAmount(reward, resources);
		if (!Number.isFinite(amount) || amount <= 0) return null;

		return {
			type: GrandPrizeRewardType.CURRENCY,
			currency,
			amount,
		};
	}

	if (reward.type !== "item") return null;

	const frameName = reward.frameName?.trim();
	if (!frameName) return null;

	const amount = reward.amount ?? 0;
	if (!Number.isFinite(amount) || amount <= 0) return null;

	return {
		type: GrandPrizeRewardType.ITEM,
		frameName: frameName as FrameName,
		amount,
	};
}
